import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    Switch,
    Typography,
} from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useMemo, useState } from 'react';
import { ProfileService } from '@layouts/pages/System/profiles/services/profile.service';
import { ProfileResponse } from '@layouts/pages/System/profiles/services/responses/profile.response';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { UserService } from 'layouts/pages/System/users/services/user.service';
import { UserRequest } from 'layouts/pages/System/users/services/requests/user.request';
import { UserResponse } from 'layouts/pages/System/users/services/responses/user.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit: UserResponse | null;
    handleModal: () => void;
    getAll: () => void;
    clearSearch: () => void;
}

const MIN_PASSWORD_LENGTH = 8;

export const UserModal = ({ open, edit, handleModal, getAll, clearSearch }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const service = new UserService();

    // Senha só no cadastro: quem edita usuários não troca a senha de outra pessoa.
    const schema = z
        .object({
            name: z.string().min(1, t('usuarios.nomeObrigatorio')).max(150),
            email: z
                .string()
                .min(1, t('usuarios.emailObrigatorio'))
                .email(t('usuarios.emailInvalido'))
                .max(150),
            active: z.boolean(),
            password: z.string().optional(),

            // Sem perfil é estado válido: a pessoa entra e não vê tela até decidirem o cargo.
            profileId: z.string().nullable().optional(),
        })
        .superRefine((payload, ctx) => {
            const password = payload.password?.trim() ?? '';

            if (!edit && password.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['password'],
                    message: t('usuarios.senhaObrigatoria'),
                });
                return;
            }

            if (!edit && password.length < MIN_PASSWORD_LENGTH) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['password'],
                    message: t('usuarios.senhaMinima', { minimo: MIN_PASSWORD_LENGTH }),
                });
            }
        });

    const [profiles, setProfiles] = useState<ProfileResponse[]>([]);

    const form = useForm<UserRequest>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', email: '', active: true, password: '', profileId: null },
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = form;

    /// O perfil atual entra na lista mesmo desativado, para editar o usuário não trocá-lo.
    const profileOptions = useMemo(() => {
        const list =
            edit?.profileId && !profiles.some((profile) => profile.id === edit.profileId)
                ? [
                      {
                          id: edit.profileId,
                          name: edit.profileName ?? '',
                          active: false,
                          screens: [],
                      } as ProfileResponse,
                      ...profiles,
                  ]
                : profiles;

        return list.map((profile) => ({
            value: profile.id,
            label: profile.active ? profile.name : `${profile.name} (${t('usuarios.inativo')})`,
        }));
    }, [profiles, edit, t]);

    // Só os ativos vêm; o perfil atual do usuário aparece mesmo desativado, senão editar o trocaria.
    useEffect(() => {
        if (!open) return;

        new ProfileService()
            .getActive()
            .then((res) => setProfiles(res.data.data ?? []))
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;

        reset(
            edit
                ? {
                      name: edit.name,
                      email: edit.email,
                      active: edit.active,
                      password: '',
                      profileId: edit.profileId ?? null,
                  }
                : { name: '', email: '', active: true, password: '', profileId: null }
        );
    }, [open]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const saveChanges = async (data: UserRequest) => {
        const newPassword = data.password?.trim() ?? '';

        setLoading(dispatch, true);

        try {
            if (edit) {
                const { password: _password, ...changes } = data;
                await service.update(edit.id, changes);
                showAlert(t('general.atualizacao'), 'success', 2500);
                getAll();
            } else {
                await service.create({ ...data, password: newPassword });
                showAlert(t('general.cadastro'), 'success', 2500);
                clearSearch();
            }

            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('usuarios.novoUsuario')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input
                                        type="text"
                                        autoComplete="off"
                                        size="medium"
                                        name="name"
                                        autoFocus
                                    />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>

                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('usuarios.email')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input
                                        type="email"
                                        autoComplete="off"
                                        size="medium"
                                        name="email"
                                    />
                                    <Form.ErrorMessage field="email" />
                                </FormControl>
                            </Form.Field>

                            {!edit && (
                                <Form.Field size={12}>
                                    <FormControl fullWidth>
                                        <Form.Label>
                                            {t('usuarios.senha')}
                                            <Form.MandatoryIcon />
                                        </Form.Label>
                                        <Form.Input
                                            type="password"
                                            autoComplete="new-password"
                                            size="medium"
                                            name="password"
                                        />
                                        <Form.ErrorMessage field="password" />
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 0.5 }}
                                        >
                                            {t('usuarios.senhaAjuda', {
                                                minimo: MIN_PASSWORD_LENGTH,
                                            })}
                                        </Typography>
                                    </FormControl>
                                </Form.Field>
                            )}

                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>{t('usuarios.perfil')}</Form.Label>
                                    <Form.AutoCompleteSelect
                                        name="profileId"
                                        placeholder={t('usuarios.semPerfil')}
                                        options={profileOptions}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                    >
                                        {t('usuarios.perfilAjuda')}
                                    </Typography>
                                </FormControl>
                            </Form.Field>

                            {/* Só na edição: o cadastro nasce ativo, e um interruptor que não obedece seria mentira. */}
                            {edit && (
                                <Form.Field size={12}>
                                    <Controller
                                        name="active"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={field.value}
                                                        onChange={(e) =>
                                                            field.onChange(e.target.checked)
                                                        }
                                                        color="success"
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {t('usuarios.ativo')}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {t('usuarios.ativoAjuda')}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        )}
                                    />
                                </Form.Field>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box></Box>
                        <Box>
                            <MDButton
                                sx={{ marginRight: '5px' }}
                                variant="gradient"
                                color="dark"
                                onClick={handleModal}
                            >
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
